import { type Ref, useState } from "react";
import * as styles from "./styles.css";
import { Icon } from "../Icon";

type ControlledProps<T> = {
  values: T[];
  defaultValues?: never;
  onChange: (values: T[]) => void;
};

type UncontrolledProps<T> = {
  values?: never;
  defaultValues: T[];
  onChange?: (values: T[]) => void;
};

export type Choice<T> = {
  id: string;
  value: T;
  label: string;
  disabled?: boolean;
  hasError?: boolean;
  ref?: Ref<HTMLInputElement>;
};

type Props<T> = (ControlledProps<T> | UncontrolledProps<T>) & {
  name?: string;
  choices: Choice<T>[];
};

export const ChoiceChip = <T extends string | number>(props: Props<T>) => {
  const { name, values, defaultValues, onChange, choices } = props;

  const [internalValues, setInternalValues] = useState<T[]>(defaultValues || []);

  const isControlled = values !== undefined;
  const currentValues = isControlled ? values : internalValues;

  const handleChange = (changedValues: T, checked: boolean) => {
    const newValues = checked
      ? [...currentValues, changedValues]
      : currentValues.filter((v) => v !== changedValues);

    if (!isControlled) setInternalValues(newValues);
    onChange?.(newValues);
  };

  return (
    <div className={styles.wrapper}>
      {!isControlled && <input type="hidden" name={name} value={JSON.stringify(currentValues)} />}
      {choices.map((choice) => (
        <label
          className={styles.inner({
            isDisabled: choice.disabled,
            hasError: choice.hasError,
          })}
          key={choice.id}
        >
          <input
            type="checkbox"
            className={styles.input}
            id={choice.id}
            value={choice.value}
            aria-label={choice.label}
            disabled={choice.disabled}
            checked={isControlled ? currentValues.includes(choice.value) : undefined}
            defaultChecked={!isControlled ? currentValues.includes(choice.value) : undefined}
            ref={choice.ref}
            onChange={(e) => handleChange(choice.value, e.target.checked)}
          />
          <span className={styles.label}>
            <div className={styles.iconWrapper}>
              <Icon name="done" fontSize="16px" />
            </div>
            {choice.label}
          </span>
        </label>
      ))}
    </div>
  );
};
